import { keyframes, Dialog, DialogContent } from '@mui/material'
import {
  Extension,      // Puzzle for QuestionAnswer
  Lock,           // Padlock for Numpad
  QrCodeScanner,  // Camera/QR for QRCode
  Security,       // Shield/Guard for ModeratorApproval
} from '@mui/icons-material'
import { cloneElement, isValidElement, ReactElement } from 'react'
import { SessionInputType } from '@api/types/session'

// Pulse glow animation for header icon
export const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 4px 2px rgba(25, 118, 210, 0.5);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 12px 6px rgba(25, 118, 210, 0.8);
    transform: scale(1.1);
  }
`

interface ChallengeDialogProps {
  open: boolean
  onClose: () => void
  children: React.ReactNode  // The input component to render in dialog
}

// Get icon based on input type
export function getChallengeIcon(inputType: SessionInputType, fontSize: number = 32) {
  switch (inputType) {
    case 'QuestionAnswer':
      return <Extension sx={{ fontSize }} />
    case 'Numpad':
      return <Lock sx={{ fontSize }} />
    case 'QRCode':
      return <QrCodeScanner sx={{ fontSize }} />
    case 'ModeratorApproval':
      return <Security sx={{ fontSize }} />
    default:
      return <Extension sx={{ fontSize }} />
  }
}

// Get color based on input type
export function getChallengeColor(inputType: SessionInputType): 'primary' | 'secondary' | 'warning' | 'info' {
  switch (inputType) {
    case 'QuestionAnswer':
      return 'primary'
    case 'Numpad':
      return 'secondary'
    case 'QRCode':
      return 'info'
    case 'ModeratorApproval':
      return 'warning'
    default:
      return 'primary'
  }
}

// Get label based on input type
export function getChallengeLabel(inputType: SessionInputType): string {
  switch (inputType) {
    case 'QuestionAnswer':
      return 'Answer Question'
    case 'Numpad':
      return 'Enter Code'
    case 'QRCode':
      return 'Scan QR Code'
    case 'ModeratorApproval':
      return 'Waiting for Approval'
    default:
      return 'Complete Challenge'
  }
}

// Dialog component - controlled externally
export default function ChallengeDialog({
  open,
  onClose,
  children,
}: ChallengeDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          // Allow children to control their own background (e.g. NumpadInput lock style)
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
        },
      }}
    >
      <DialogContent sx={{ p: 0, overflow: 'visible' }}>
        {/* Pass onSubmitSuccess and onClose callbacks to children */}
        {isValidElement(children)
          ? cloneElement(children as ReactElement<{ onSubmitSuccess?: () => void; onClose?: () => void }>, {
              onSubmitSuccess: onClose,
              onClose,
            })
          : children}
      </DialogContent>
    </Dialog>
  )
}
